import { ISimplifiedVisualMaterial } from '@libs/api'
import { useLocalization } from '@libs/localization'
import { isVideoFormat } from '@libs/util'
import { Tag, Tooltip } from 'antd'
import React, { FC, SyntheticEvent, useCallback, useMemo } from 'react'
import { DEFAULT_IMG_SOURCE } from '.'

import Styled from './Styled'
import { Grid } from '@libs/components'

export interface IMaterialItemThumbnail
	extends Pick<
		ISimplifiedVisualMaterial,
		| 'thumbnailUrl'
		| 'source'
		| 'sourceType'
		| 'updatedDate'
		| 'description'
		| 'isActive'
		| 'id'
		| 'fileName'
		| 'thumbnailOrientation'
	> {
	/** Image click handler */
	onImageClick?: (id: ISimplifiedVisualMaterial['id']) => void
	tags: string[]
	/** Applies vertical wrapper styles */
	forceVerticalLayout?: boolean
	/** Removes overlay for inactive materials */
	disabledInactiveOverlay?: boolean
	showDisabledInteraction?: boolean
	isIFPMaterial: boolean
	showHighQualityImage?: boolean
}

const MaterialItemThumbnail: FC<IMaterialItemThumbnail> = React.memo(
	({
		tags,
		fileName,
		onImageClick,
		id,
		forceVerticalLayout,
		thumbnailOrientation,
		showDisabledInteraction,
		disabledInactiveOverlay,
		isActive,
		thumbnailUrl,
		source,
		sourceType,
		isIFPMaterial,
		showHighQualityImage,
	}) => {
		const { t } = useLocalization()
		const onImageLoadError = useCallback(
			(event: SyntheticEvent<HTMLImageElement, Event>) => ((event.target as HTMLImageElement).src = DEFAULT_IMG_SOURCE),
			[]
		)

		const isVideo = useMemo(
			() => isVideoFormat(thumbnailUrl) || (!thumbnailUrl && sourceType.toLowerCase() === 'video'),
			[thumbnailUrl, sourceType]
		)

		const imgSrc = useMemo(
			() =>
				isIFPMaterial
					? '/placeholders/ifp-placeholder.jpeg'
					: showHighQualityImage
					? source
					: thumbnailUrl || DEFAULT_IMG_SOURCE,
			[showHighQualityImage, source, thumbnailUrl, isIFPMaterial]
		)
		return (
			<Tooltip title={fileName}>
				<Styled.ImageWrapper
					$clickable={!!onImageClick}
					onClick={() => onImageClick?.(id)}
					$forceVerticalLayout={forceVerticalLayout}
					$orentation={thumbnailOrientation}
				>
					{((!disabledInactiveOverlay && !isActive) || showDisabledInteraction) && <Styled.InactiveOverlay />}
					{isVideo ? (
						<video width="100%" height="100%" preload="metadata">
							<source src={thumbnailUrl || `${source}#t=1.3`} />
						</video>
					) : (
						<img
							src={imgSrc}
							onError={onImageLoadError}
							style={{
								WebkitTouchCallout: 'none',
								userSelect: 'none',
							}}
							alt={fileName}
						/>
					)}
					{tags.length > 0 && (
						<Styled.MaterialTagList gutter="xxs">
							{tags.map(tagTextKey => (
								<Grid.Col key={tagTextKey}>
									<Tag color="gold">{t(`materials.tags.${tagTextKey}`)}</Tag>
								</Grid.Col>
							))}
						</Styled.MaterialTagList>
					)}
				</Styled.ImageWrapper>
			</Tooltip>
		)
	}
)

export default MaterialItemThumbnail
